/*
  assets/gtender-pro-capability.js — GTENDER-PRO-CAPABILITY-02
  Shared presentation/capability-check owner.
  Backend /v1/gtender/access is authoritative. This runtime never grants Pro.
  Public Cloud Save consumes the existing native Saved ingress only after verified Pro access.
*/
(function () {
  'use strict';

  var GLOBAL_KEY = '__GTENDER_PRO_CAPABILITY_RUNTIME__';
  var TRIGGER_SELECTOR = '[data-gtender-pro-capability]';
  var DIALOG_SELECTOR = '[data-gtender-pro-capability-dialog]';
  var PUBLIC_ROOT_SELECTOR = '[data-gtender-public-entry]';
  var ACCESS_URL = 'https://api.glhksolution.com/v1/gtender/access';
  var SAVE_URL = 'https://api.glhksolution.com/v1/tender/save';
  var REQUEST_TIMEOUT_MS = 10000;
  var TENDER_ID_RE = /^[A-Za-z0-9._:-]{3,240}$/;
  var BUSINESS_MODAL_SELECTOR = [
    '[data-gtender-public-detail][open]',
    '[data-gtender-auth-sheet][open]',
    'dialog[id^="GCPBriefDialog-"][open]'
  ].join(',');

  var COPY = {
    'cloud-save': {
      zh: ['雲端收藏與同步', '把招標保存到你的 GTender Pro 工作區，跨裝置繼續跟進。', ['雲端保存招標', '在 My Tender Workspace 持續跟進']],
      en: ['Cloud saves and sync', 'Save tenders to your GTender Pro workspace and continue across devices.', ['Cloud-backed tender saves', 'Continue in My Tender Workspace']]
    },
    evidence: {
      zh: ['Evidence 狀態追蹤', '保存投標要求、證明文件狀態及缺漏項目。', ['Missing / Requested / Ready / Verified', '按招標保存 Evidence 狀態']],
      en: ['Evidence tracking', 'Save requirement, evidence and readiness states for the tender.', ['Missing / Requested / Ready / Verified', 'Tender-scoped evidence status']]
    },
    pipeline: {
      zh: ['Pipeline 與 Bid / No-Bid 決策', '把機會推進到 review、bid/no-bid、prepare 及 closed。', ['保存 Pipeline stage', '保存 Bid / No-Bid 決策與原因']],
      en: ['Pipeline and Bid / No-Bid', 'Move opportunities through review, bid/no-bid, prepare and closed.', ['Saved pipeline stages', 'Bid / No-Bid decision and reason']]
    },
    watch: {
      zh: ['Watch Rules 與提醒紀錄', '建立監察條件並集中查看招標變更；自動提醒發送目前仍未啟用。', ['保存 Watch Rules', '查看招標變更與提醒紀錄（自動發送停用）']],
      en: ['Watch Rules and alert records', 'Create monitoring rules and review tender changes in one place. Automated alert delivery is currently disabled.', ['Saved Watch Rules', 'Review tender changes and alert records (delivery off)']]
    },
    generate: {
      zh: ['自動生成能力', '使用需要 Pro 權限的自動化工作流程；高成本 AI 使用仍可按 GFun 規則計量。', ['Pro 自動化入口', 'GTender 帳戶與 Pro 權限保持分離驗證']],
      en: ['Automated generation', 'Use Pro-gated automation; higher-cost AI usage may still be metered under GFun rules.', ['Pro automation entry', 'Account identity and Pro entitlement stay separately verified']]
    }
  };

  if (window[GLOBAL_KEY]) return;

  var lastOpener = null;
  var projectionObserver = null;
  var projectionFrame = 0;

  function clean(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  function raw(value) {
    return String(value == null ? '' : value).trim();
  }

  function language(dialog) {
    var locale = clean(document.documentElement.lang || dialog && dialog.closest('[data-locale]') && dialog.closest('[data-locale]').getAttribute('data-locale'));
    return locale.indexOf('zh') === 0 ? 'zh' : 'en';
  }

  function cloudSaveText(key) {
    var zh = language(null) === 'zh';
    var messages = {
      idle: zh ? '保存到 Pro' : 'Save to Pro',
      saving: zh ? '正在保存…' : 'Saving…',
      saved: zh ? '已保存到 Pro' : 'Saved to Pro',
      retry: zh ? '重試保存到 Pro' : 'Retry Save to Pro'
    };
    return messages[key] || messages.idle;
  }

  function dispatch(target, name, detail) {
    (target || document).dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} }));
  }

  function findDialog(trigger) {
    var root = trigger && trigger.closest ? trigger.closest('[data-gtender-dashboard-root], [data-gtender-public-entry], [data-gcitypartners-dashboard]') : null;
    var scoped = root && root.querySelector ? root.querySelector(DIALOG_SELECTOR) : null;
    return scoped || document.querySelector(DIALOG_SELECTOR);
  }

  function anotherBusinessModalIsOpen(dialog) {
    var open = document.querySelector(BUSINESS_MODAL_SELECTOR);
    return !!(open && open !== dialog);
  }

  function setFeatures(dialog, items) {
    var list = dialog.querySelector('[data-gtender-pro-capability-features]');
    if (!list) return;
    list.replaceChildren();
    items.forEach(function (text) {
      var li = document.createElement('li');
      li.textContent = text;
      list.appendChild(li);
    });
  }

  function setUnavailable(dialog) {
    var lang = language(dialog);
    var title = dialog.querySelector('[data-gtender-pro-capability-title]');
    var copy = dialog.querySelector('[data-gtender-pro-capability-copy]');
    var status = dialog.querySelector('[data-gtender-pro-capability-status]');
    var upgrade = dialog.querySelector('[data-gtender-pro-capability-upgrade]');
    if (title) title.textContent = lang === 'zh' ? '暫時無法確認 Pro 權限' : 'Unable to verify Pro access';
    if (copy) copy.textContent = lang === 'zh' ? '目前無法安全確認你的 GTender 方案。請稍後再試；你的帳戶權限不會因此改變。' : 'We cannot safely verify your GTender plan right now. Try again later; your account access has not been changed.';
    if (status) status.textContent = lang === 'zh' ? '未進行任何權限變更。' : 'No access change was made.';
    if (upgrade) upgrade.hidden = true;
    setFeatures(dialog, []);
  }

  function setCapability(dialog, capability) {
    var lang = language(dialog);
    var data = COPY[capability] && COPY[capability][lang];
    if (!data) return false;
    var title = dialog.querySelector('[data-gtender-pro-capability-title]');
    var copy = dialog.querySelector('[data-gtender-pro-capability-copy]');
    var status = dialog.querySelector('[data-gtender-pro-capability-status]');
    var upgrade = dialog.querySelector('[data-gtender-pro-capability-upgrade]');
    if (title) title.textContent = data[0];
    if (copy) copy.textContent = data[1];
    if (status) status.textContent = lang === 'zh' ? 'GTender Pro 只會在後端驗證成功後啟用。' : 'GTender Pro unlocks only after backend verification.';
    if (upgrade) {
      upgrade.hidden = false;
      var url = dialog.getAttribute('data-pro-url');
      if (url) upgrade.setAttribute('href', url);
    }
    setFeatures(dialog, data[2]);
    dialog.setAttribute('data-capability', capability);
    return true;
  }

  function focusable(dialog) {
    return Array.prototype.slice.call(dialog.querySelectorAll('a[href]:not([hidden]), button:not([disabled]):not([hidden]), [tabindex]:not([tabindex="-1"]):not([hidden])'));
  }

  function openDialog(dialog, opener) {
    if (!dialog || anotherBusinessModalIsOpen(dialog)) return false;
    lastOpener = opener || null;
    try {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    } catch (_) {
      dialog.setAttribute('open', '');
    }
    var first = focusable(dialog)[0];
    if (first) first.focus();
    return true;
  }

  function closeDialog(dialog) {
    if (!dialog) return;
    try {
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
    } catch (_) {
      dialog.removeAttribute('open');
    }
    var opener = lastOpener;
    lastOpener = null;
    if (opener && document.documentElement.contains(opener) && typeof opener.focus === 'function') opener.focus();
  }

  function withTimeoutOptions(method, headers) {
    if (typeof AbortController !== 'function') {
      return { options: { method: method || 'GET', headers: headers || { Accept: 'application/json' } }, cancel: function () {} };
    }
    var controller = new AbortController();
    var timer = window.setTimeout(function () { controller.abort(); }, REQUEST_TIMEOUT_MS);
    return {
      options: { method: method || 'GET', headers: headers || { Accept: 'application/json' }, signal: controller.signal },
      cancel: function () { window.clearTimeout(timer); }
    };
  }

  async function resolveAccess() {
    var session = window.LittleGWebSession;
    if (!session || typeof session.getState !== 'function') return { kind: 'guest' };
    try {
      if (typeof session.ready === 'function') await session.ready();
    } catch (_) {}
    var state = session.getState();
    if (clean(state && state.status) !== 'authenticated') return { kind: 'guest' };
    if (typeof session.authenticatedFetch !== 'function') throw new Error('authenticated_fetch_unavailable');

    var timed = withTimeoutOptions('GET', { Accept: 'application/json' });
    try {
      var response = await session.authenticatedFetch(ACCESS_URL, timed.options);
      var payload = await response.json().catch(function () { return {}; });
      if (!response.ok || payload.success !== true || !payload.data) throw new Error('gtender_access_unavailable');
      var mode = clean(payload.data.mode);
      if (mode === 'pro' && payload.data.pro_entitled === true) return { kind: 'pro', data: payload.data };
      return { kind: 'free', data: payload.data };
    } finally {
      timed.cancel();
    }
  }

  function openExistingAuthForPro(trigger) {
    var authTrigger = document.querySelector('[data-context-product="gtender"] [data-gtender-native-auth-open]') || document.querySelector('[data-gtender-native-auth-open]');
    if (!authTrigger || typeof authTrigger.click !== 'function') return false;
    var previous = authTrigger.getAttribute('data-gtender-plan-intent');
    authTrigger.setAttribute('data-gtender-plan-intent', 'pro');
    authTrigger.click();
    if (previous == null) authTrigger.removeAttribute('data-gtender-plan-intent');
    else authTrigger.setAttribute('data-gtender-plan-intent', previous);
    dispatch(trigger, 'gtender:pro-capability-auth-forwarded', { capability: 'cloud-save' });
    return true;
  }

  async function saveCloudTender(trigger) {
    var id = raw(trigger && trigger.getAttribute('data-gtender-cloud-save-id'));
    var session = window.LittleGWebSession;
    if (!TENDER_ID_RE.test(id) || !session || typeof session.authenticatedFetch !== 'function') return false;
    if (trigger.getAttribute('data-gtender-cloud-save-state') === 'saved') return true;

    trigger.disabled = true;
    trigger.setAttribute('data-gtender-cloud-save-state', 'saving');
    trigger.textContent = cloudSaveText('saving');
    var timed = withTimeoutOptions('POST', { Accept: 'application/json', 'Content-Type': 'application/json' });
    timed.options.body = JSON.stringify({ tender_id: id });

    try {
      var response = await session.authenticatedFetch(SAVE_URL, timed.options);
      var payload = await response.json().catch(function () { return {}; });
      if (!response.ok || payload.success !== true || !payload.data || !payload.data.item) {
        var error = new Error(payload && payload.error && payload.error.code || 'gtender_cloud_save_failed');
        error.status = response.status;
        throw error;
      }
      trigger.setAttribute('data-gtender-cloud-save-state', 'saved');
      trigger.textContent = cloudSaveText('saved');
      trigger.disabled = true;
      dispatch(trigger, 'gtender:cloud-save:changed', { tender_id: id, saved: true });
      return true;
    } catch (error) {
      trigger.setAttribute('data-gtender-cloud-save-state', 'error');
      trigger.textContent = cloudSaveText('retry');
      trigger.disabled = false;
      if (Number(error && error.status) === 401 || Number(error && error.status) === 403) {
        dispatch(trigger, 'gtender:pro-capability-auth-required', { capability: 'cloud-save' });
      }
      return false;
    } finally {
      timed.cancel();
    }
  }

  async function onCapabilityTrigger(trigger, capability) {
    var dialog = findDialog(trigger);
    if (!dialog) return;
    if (anotherBusinessModalIsOpen(dialog)) {
      dispatch(trigger, 'gtender:pro-capability-blocked-by-modal', { capability: capability });
      return;
    }

    trigger.setAttribute('aria-busy', 'true');
    var deadlineTimer = null;
    try {
      var access = await Promise.race([
        resolveAccess(),
        new Promise(function (_, reject) {
          deadlineTimer = window.setTimeout(function () {
            var timeout = new Error('GTender Pro access verification timed out.');
            timeout.code = 'gtender_access_timeout';
            reject(timeout);
          }, REQUEST_TIMEOUT_MS);
        })
      ]);
      if (access.kind === 'pro') {
        dispatch(trigger, 'gtender:pro-capability-allowed', { capability: capability, access: access.data });
        if (capability === 'cloud-save') await saveCloudTender(trigger);
        return;
      }
      if (access.kind === 'guest') {
        dispatch(trigger, 'gtender:pro-capability-auth-required', { capability: capability });
        if (capability === 'cloud-save') openExistingAuthForPro(trigger);
        return;
      }
      if (!setCapability(dialog, capability)) return;
      openDialog(dialog, trigger);
    } catch (_) {
      setUnavailable(dialog);
      openDialog(dialog, trigger);
    } finally {
      if (deadlineTimer !== null) window.clearTimeout(deadlineTimer);
      trigger.removeAttribute('aria-busy');
    }
  }

  function projectCloudSaveButtons(scope) {
    var target = scope || document;
    var roots = [];
    if (target.matches && target.matches(PUBLIC_ROOT_SELECTOR)) roots.push(target);
    if (target.querySelectorAll) Array.prototype.push.apply(roots, target.querySelectorAll(PUBLIC_ROOT_SELECTOR));

    roots.forEach(function (root) {
      root.querySelectorAll('[data-gtender-detail-open]').forEach(function (detailTrigger) {
        var id = raw(detailTrigger.getAttribute('data-gtender-detail-open'));
        if (!TENDER_ID_RE.test(id)) return;
        var card = detailTrigger.closest('.gtender-partner-entry__card, article');
        var actions = card && card.querySelector ? card.querySelector('.gtender-card__action-row') : null;
        if (!actions || actions.querySelector('[data-gtender-cloud-save-id]')) return;

        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'gtender-partner-entry__button';
        button.setAttribute('data-gtender-pro-capability', 'cloud-save');
        button.setAttribute('data-gtender-cloud-save-id', id);
        button.setAttribute('data-gtender-cloud-save-state', 'idle');
        button.textContent = cloudSaveText('idle');
        actions.appendChild(button);
      });
    });
  }

  function scheduleProjection() {
    if (projectionFrame) return;
    projectionFrame = window.requestAnimationFrame(function () {
      projectionFrame = 0;
      projectCloudSaveButtons(document);
    });
  }

  function onClick(event) {
    var close = event.target && event.target.closest ? event.target.closest('[data-gtender-pro-capability-close]') : null;
    if (close) {
      var closeDialogNode = close.closest(DIALOG_SELECTOR);
      if (closeDialogNode) {
        event.preventDefault();
        closeDialog(closeDialogNode);
      }
      return;
    }

    var trigger = event.target && event.target.closest ? event.target.closest(TRIGGER_SELECTOR) : null;
    if (!trigger) return;
    var capability = clean(trigger.getAttribute('data-gtender-pro-capability'));
    if (!COPY[capability]) return;
    event.preventDefault();
    onCapabilityTrigger(trigger, capability);
  }

  function onKeydown(event) {
    var dialog = document.querySelector(DIALOG_SELECTOR + '[open]');
    if (!dialog) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDialog(dialog);
      return;
    }
    if (event.key !== 'Tab') return;
    var nodes = focusable(dialog);
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  document.addEventListener('click', onClick);
  document.addEventListener('keydown', onKeydown);
  document.addEventListener('cancel', function (event) {
    if (event.target && event.target.matches && event.target.matches(DIALOG_SELECTOR)) {
      event.preventDefault();
      closeDialog(event.target);
    }
  }, true);
  document.addEventListener('shopify:section:load', function (event) {
    projectCloudSaveButtons(event.target || document);
  });

  projectCloudSaveButtons(document);
  if (typeof MutationObserver === 'function') {
    projectionObserver = new MutationObserver(scheduleProjection);
    projectionObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  window[GLOBAL_KEY] = Object.freeze({
    version: 'gtender-pro-capability-02',
    accessUrl: ACCESS_URL,
    saveUrl: SAVE_URL,
    project: projectCloudSaveButtons
  });
})();